import { AuthConsumerBody, AuthConsumerResponse } from "./types"
import { MAX_AGE, Session, State } from "./state"

import { API } from "./api"
import { SDKOptions } from "./sdk"
import { getPayload } from "./utils/payload"

const MAX_RETRY_DELAY = 60000

export class Authentication {
    options: SDKOptions
    state: State
    api: {
        post: API["post"]
    }
    #pendingAuthConsumer?: Promise<void>
    #retryTimeout?: ReturnType<typeof setTimeout>
    #retryAttempt = 0

    constructor(options: SDKOptions, state: State, api: API) {
        this.options = options
        this.state = state
        this.api = api

        this.init()
    }

    init() {
        if (this.state.consumerToken) {
            this.state.consumerTokenStatus = "success"
            return
        }

        this.authConsumer()
    }

    validateToken(token?: string, interval = 0) {
        if (!token) {
            return false
        }

        const payload = getPayload(token)

        if (!payload.exp) {
            return false
        }

        return payload.exp - Date.now() >= interval
    }

    // every way of opening a session goes through here, so none of them can navigate away before
    // the cookies are written
    async setSession(session: Session) {
        this.state.setSession(session)

        if (!this.options.serverSideCookies) return

        // a failed cookie call must not fail the login. the session already lives in the state and
        // in the client side cookies
        await Promise.allSettled([
            this.api.post("/cookies/set", "none", {
                body: {
                    key: `${this.options.storageKey}-customerWeakToken`,
                    value: session.token.weak.value,
                    max_age: MAX_AGE.customerWeakToken,
                },
                credentials: "include",
            }),
            this.api.post("/cookies/set", "none", {
                body: {
                    key: `${this.options.storageKey}-customer_id`,
                    value: session.customer_id,
                    max_age: MAX_AGE.customer_id,
                },
                credentials: "include",
            }),
        ])
    }

    logout() {
        this.state.customerWeakToken = undefined
        this.state.customerStrongToken = undefined
        this.state.customer_id = undefined
        this.state.email = undefined
        this.state.cart_id = undefined
        this.state.cart_access_token = undefined

        return
    }

    async authConsumer() {
        if (this.#pendingAuthConsumer) return await this.#pendingAuthConsumer

        this.#pendingAuthConsumer = this.requestConsumerToken()

        try {
            return await this.#pendingAuthConsumer
        } finally {
            this.#pendingAuthConsumer = undefined
        }
    }

    async requestConsumerToken() {
        this.state.consumerTokenStatus = "pending"

        const body: AuthConsumerBody = {
            uid: this.options.uid,
            password: this.options.password,
        }

        try {
            const response = await this.api.post<AuthConsumerResponse>("/auth/consumer", "none", { body: body })

            this.state.consumerToken = response.token
            this.state.consumerTokenStatus = "success"

            this.#retryAttempt = 0
            this.#clearRetry()
        } catch {
            this.state.consumerTokenStatus = "error"
            this.#scheduleRetry()
        }
        return
    }

    #scheduleRetry = () => {
        if (typeof window === "undefined") return

        this.#clearRetry()

        const delay = Math.min(MAX_RETRY_DELAY, 1000 * 2 ** this.#retryAttempt)
        this.#retryAttempt++

        this.#retryTimeout = setTimeout(() => {
            this.#retryTimeout = undefined
            this.authConsumer()
        }, delay)
    }

    #clearRetry = () => {
        if (this.#retryTimeout === undefined) return

        clearTimeout(this.#retryTimeout)
        this.#retryTimeout = undefined
    }

    retryNow() {
        if (this.state.consumerTokenStatus !== "error") return

        this.#retryAttempt = 0
        this.#clearRetry()
        this.authConsumer()
    }
}
